import { useApp } from "@/contexts/AppContext";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../ui/select";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "../ui/card";
import { Badge } from "../ui/badge";
import { Progress } from "../ui/progress";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "../ui/alert-dialog";
import {
  AlertTriangle,
  Undo2,
  Loader2,
  ChevronLeft,
  ChevronRight,
  Filter,
  RefreshCw,
  Play,
  FileText,
  HardDrive,
} from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import { databaseHealth, healthPing } from "@/services/health.service";
import {
  getSearchIndexStatus,
  getAdminLogs,
  getAllUserActivities,
  getInstanceMigrationStatus,
  getLatestStatisticsRebuild,
  restoreActivity,
  startInstanceMigration,
  startSearchIndexRebuild,
  startStatisticsRebuild,
} from "@/services/adminSysten.service";
import { AccountActivity } from "@/types/activity.types";
import {
  AdminLogEntry,
  InstanceMigrationJob,
  InstanceMigrationStartRequest,
  InstanceMigrationStatus,
  SearchIndexStatus,
  StatisticsRebuildJob,
} from "@/types/adminSystem.types";
import { formatDistanceToNow } from "date-fns";
import { de, enUS } from "date-fns/locale";
import { toast } from "sonner";

interface SystemPanelProps {
  navigate: (page: string) => void;
}

const ITEMS_PER_PAGE = 5;
const LOGS_PAGE_SIZE = 30;

export function SystemPanel({ navigate }: SystemPanelProps) {
  const { t, language } = useApp();
  const iconStyle = {
    color:
      "color-mix(in srgb, color-mix(in srgb, var(--primary) 70%, var(--accent)) 90%, var(--foreground))",
  } as const;
  const primaryTextStyle = {
    color: "color-mix(in srgb, var(--foreground) 77%, var(--surface))",
  } as const;
  const secondaryTextStyle = {
    color: "color-mix(in srgb, var(--foreground) 66%, var(--surface))",
  } as const;
  const borderStyle = {
    borderColor: "color-mix(in srgb, var(--foreground) 12%, var(--background))",
  } as const;
  const actionLabelStyles = {
    delete: {
      color: "color-mix(in srgb, var(--accent) 72%, var(--foreground))",
    },
    restore: {
      color: "color-mix(in srgb, var(--primary) 70%, var(--foreground))",
    },
    update: {
      color: "color-mix(in srgb, var(--primary) 55%, var(--foreground))",
    },
    suggest: {
      color: "color-mix(in srgb, var(--accent) 55%, var(--foreground))",
    },
  } as const;
  const statusStyles = {
    online: {
      backgroundColor:
        "color-mix(in srgb, var(--primary) 18%, var(--background))",
      color: "color-mix(in srgb, var(--foreground) 82%, var(--surface))",
      borderColor:
        "color-mix(in srgb, var(--primary) 28%, var(--background))",
    },
    checking: {
      backgroundColor:
        "color-mix(in srgb, var(--accent) 18%, var(--background))",
      color: "color-mix(in srgb, var(--foreground) 82%, var(--surface))",
      borderColor:
        "color-mix(in srgb, var(--accent) 28%, var(--background))",
    },
    offline: {
      backgroundColor:
        "color-mix(in srgb, var(--foreground) 10%, var(--background))",
      color: "color-mix(in srgb, var(--foreground) 70%, var(--surface))",
      borderColor:
        "color-mix(in srgb, var(--foreground) 18%, var(--background))",
    },
  } as const;

  const [apiStatus, setApiStatus] = useState<"checking" | "online" | "offline">(
    "checking"
  );
  const [dbStatus, setDbStatus] = useState<"checking" | "online" | "offline">(
    "checking"
  );

  const [activities, setActivities] = useState<AccountActivity[]>([]);
  const [loadingActivities, setLoadingActivities] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);

  const [rebuildJob, setRebuildJob] = useState<StatisticsRebuildJob | null>(null);
  const [startingRebuild, setStartingRebuild] = useState(false);
  const [rebuildConfirmOpen, setRebuildConfirmOpen] = useState(false);
  const [instanceMigrationStatus, setInstanceMigrationStatus] = useState<InstanceMigrationStatus | null>(null);
  const [startingInstanceMigration, setStartingInstanceMigration] = useState(false);
  const [instanceMigrationConfirmOpen, setInstanceMigrationConfirmOpen] = useState(false);
  const [instanceMigrationSourceBaseUrl, setInstanceMigrationSourceBaseUrl] = useState("");
  const [instanceMigrationSharedToken, setInstanceMigrationSharedToken] = useState("");
  const [instanceMigrationSourceAdminEmail, setInstanceMigrationSourceAdminEmail] = useState("");
  const [instanceMigrationSourceAdminPassword, setInstanceMigrationSourceAdminPassword] = useState("");
  const [instanceMigrationPageSize, setInstanceMigrationPageSize] = useState("");
  const [instanceMigrationRequestTimeoutMs, setInstanceMigrationRequestTimeoutMs] = useState("3600000");
  const [searchIndexStatus, setSearchIndexStatus] = useState<SearchIndexStatus | null>(null);
  const [startingSearchIndexRebuild, setStartingSearchIndexRebuild] = useState(false);
  const [searchIndexRebuildConfirmOpen, setSearchIndexRebuildConfirmOpen] = useState(false);

  const [logSource, setLogSource] = useState<"" | "application" | "parquet">(
    ""
  );
  const [logLevel, setLogLevel] = useState<
    "" | "TRACE" | "DEBUG" | "INFO" | "WARN" | "ERROR"
  >("");
  const [logLoggerContains, setLogLoggerContains] = useState("");
  const [logQuery, setLogQuery] = useState("");
  const [logEntries, setLogEntries] = useState<AdminLogEntry[]>([]);
  const [logsPage, setLogsPage] = useState(1);
  const [logsTotalPages, setLogsTotalPages] = useState(1);
  const [logsTotalElements, setLogsTotalElements] = useState(0);
  const [loadingLogs, setLoadingLogs] = useState(false);

  const errorTests = [
    { page: "error-404", label: t("admin.error404") },
    { page: "error-403", label: t("admin.error403") },
    { page: "error-500", label: t("admin.error500") },
    { page: "error-maintenance", label: t("admin.errorMaintenance") },
  ];

  const loadActivities = async () => {
    try {
      setLoadingActivities(true);
      const data = await getAllUserActivities();
      setActivities(data);
    } catch {
      toast.error(t("admin.useractivityFetchingFailed"));
    } finally {
      setLoadingActivities(false);
    }
  };

  const loadLatestRebuildJob = async () => {
    try {
      const latest = await getLatestStatisticsRebuild();
      setRebuildJob(latest);
    } catch {
      toast.error(t("admin.systemRebuildLoadFailed"));
    }
  };

  const loadSearchIndexStatus = async () => {
    try {
      const latest = await getSearchIndexStatus();
      setSearchIndexStatus(latest);
    } catch {
      toast.error(t("admin.searchIndexRebuildLoadFailed"));
    }
  };

  const loadInstanceMigrationStatus = async () => {
    try {
      const latest = await getInstanceMigrationStatus();
      setInstanceMigrationStatus(latest);
      setInstanceMigrationSourceBaseUrl((previous) =>
        previous.trim() ? previous : latest.job?.sourceBaseUrl || latest.sourceBaseUrl || ""
      );
      setInstanceMigrationPageSize((previous) =>
        previous.trim() ? previous : String(latest.pageSize || 1000)
      );
    } catch {
      toast.error(t("admin.instanceMigrationLoadFailed"));
    }
  };

  const loadLogs = async (targetPage: number = logsPage) => {
    try {
      setLoadingLogs(true);
      const response = await getAdminLogs({
        source: logSource,
        level: logLevel,
        loggerContains: logLoggerContains,
        query: logQuery,
        page: Math.max(0, targetPage - 1),
        size: LOGS_PAGE_SIZE,
      });
      setLogEntries(response.items ?? []);
      setLogsTotalElements(response.totalElements ?? 0);
      setLogsTotalPages(Math.max(1, response.totalPages ?? 1));
      setLogsPage((response.page ?? 0) + 1);
    } catch {
      toast.error(t("admin.systemLogsLoadFailed"));
      setLogEntries([]);
      setLogsTotalElements(0);
      setLogsTotalPages(1);
      setLogsPage(1);
    } finally {
      setLoadingLogs(false);
    }
  };

  useEffect(() => {
    let cancelled = false;

    const checkHealth = async () => {
      try {
        const ok = await healthPing();
        if (!cancelled) {
          setApiStatus(ok ? "online" : "offline");
        }
      } catch {
        if (!cancelled) {
          setApiStatus("offline");
        }
      }
    };

    const checkDb = async () => {
      try {
        const ok = await databaseHealth();
        if (!cancelled) {
          setDbStatus(ok ? "online" : "offline");
        }
      } catch {
        if (!cancelled) {
          setDbStatus("offline");
        }
      }
    };

    checkHealth();
    checkDb();
    loadActivities();
    loadLatestRebuildJob();
    loadInstanceMigrationStatus();
    loadSearchIndexStatus();
    loadLogs(1);

    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    if (rebuildJob?.status !== "RUNNING") {
      return;
    }
    const timer = window.setInterval(() => {
      loadLatestRebuildJob();
    }, 3000);
    return () => window.clearInterval(timer);
  }, [rebuildJob?.status]);

  useEffect(() => {
    const status = instanceMigrationStatus?.job?.status;
    if (status !== "QUEUED" && status !== "RUNNING") {
      return;
    }
    const timer = window.setInterval(() => {
      loadInstanceMigrationStatus();
    }, 3000);
    return () => window.clearInterval(timer);
  }, [instanceMigrationStatus?.job?.status]);

  useEffect(() => {
    if (!searchIndexStatus?.rebuildRunning) {
      return;
    }
    const timer = window.setInterval(() => {
      loadSearchIndexStatus();
    }, 3000);
    return () => window.clearInterval(timer);
  }, [searchIndexStatus?.rebuildRunning]);

  const paginatedActivities = useMemo(() => {
    const startIndex = (currentPage - 1) * ITEMS_PER_PAGE;
    const endIndex = startIndex + ITEMS_PER_PAGE;
    return activities.slice(startIndex, endIndex);
  }, [activities, currentPage]);

  const totalPages = Math.max(1, Math.ceil(activities.length / ITEMS_PER_PAGE));

  useEffect(() => {
    if (currentPage > totalPages) {
      setCurrentPage(totalPages);
    }
  }, [totalPages, currentPage]);

  const handleUndo = async (activityId: string) => {
    try {
      await restoreActivity(activityId);
      toast.success(t("admin.undoSuccess"));
      await loadActivities();
    } catch {
      toast.error(t("admin.undoFailed"));
    }
  };

  const handleStartRebuild = async () => {
    try {
      setStartingRebuild(true);
      const job = await startStatisticsRebuild();
      setRebuildJob(job);
      toast.success(t("admin.systemRebuildStarted"));
    } catch {
      toast.error(t("admin.systemRebuildStartFailed"));
    } finally {
      setStartingRebuild(false);
    }
  };

  const handleStartSearchIndexRebuild = async () => {
    try {
      setStartingSearchIndexRebuild(true);
      const result = await startSearchIndexRebuild();
      await loadSearchIndexStatus();
      if (result.accepted) {
        toast.success(t("admin.searchIndexRebuildStarted"));
      } else {
        toast.error(result.message || t("admin.searchIndexRebuildStartFailed"));
      }
    } catch {
      toast.error(t("admin.searchIndexRebuildStartFailed"));
    } finally {
      setStartingSearchIndexRebuild(false);
    }
  };

  const handleStartInstanceMigration = async () => {
    try {
      setStartingInstanceMigration(true);
      const request: InstanceMigrationStartRequest = {
        sourceBaseUrl: instanceMigrationSourceBaseUrl.trim() || undefined,
        sharedToken: instanceMigrationSharedToken.trim() || undefined,
        sourceAdminEmail: instanceMigrationSourceAdminEmail.trim() || undefined,
        sourceAdminPassword: instanceMigrationSourceAdminPassword || undefined,
        pageSize:
          instanceMigrationPageSize.trim() &&
          Number.isFinite(Number(instanceMigrationPageSize))
            ? Math.max(1, Number(instanceMigrationPageSize))
            : undefined,
        requestTimeoutMs:
          instanceMigrationRequestTimeoutMs.trim() &&
          Number.isFinite(Number(instanceMigrationRequestTimeoutMs))
            ? Math.max(500, Number(instanceMigrationRequestTimeoutMs))
            : undefined,
      };
      const job = await startInstanceMigration(request);
      setInstanceMigrationStatus((previous) => ({
        featureEnabled: previous?.featureEnabled ?? true,
        sourceBaseUrl: previous?.sourceBaseUrl ?? job.sourceBaseUrl,
        pageSize: request.pageSize ?? previous?.pageSize ?? 0,
        job,
        storage: previous?.storage ?? null,
      }));
      setInstanceMigrationSourceAdminPassword("");
      toast.success(t("admin.instanceMigrationStarted"));
    } catch {
      toast.error(t("admin.instanceMigrationStartFailed"));
    } finally {
      setStartingInstanceMigration(false);
    }
  };

  const handleApplyLogFilters = () => {
    setLogsPage(1);
    loadLogs(1);
  };

  const getActionDescription = (activity: AccountActivity) => {
    switch (activity.action) {
      case "DELETE":
        return (
          <span>
            <span className="font-semibold" style={actionLabelStyles.delete}>
              {t("activity.DELETE")}:{" "}
            </span>
            {activity.title || t("admin.systemPanelFallbackItem")}
          </span>
        );
      case "RESTORE":
        return (
          <span>
            <span className="font-semibold" style={actionLabelStyles.restore}>
              {t("activity.RESTORE")}:{" "}
            </span>
            {activity.title || t("admin.systemPanelFallbackItem")}
          </span>
        );
      case "UPDATE":
        return (
          <span>
            <span className="font-semibold" style={actionLabelStyles.update}>
              {t("activity.UPDATE")}:{" "}
            </span>
            {activity.title || t("admin.systemPanelFallbackItem")}
          </span>
        );
      case "SUGGEST":
        return (
          <span>
            <span className="font-semibold" style={actionLabelStyles.suggest}>
              {t("activity.SUGGEST")}:{" "}
            </span>
            {activity.title || t("admin.systemPanelFallbackSuggestion")}
          </span>
        );
      default:
        return (
          <span>
            {activity.action}: {activity.title}
          </span>
        );
    }
  };

  const apiBadge = useMemo(() => {
    if (apiStatus === "online") {
      return {
        label: t("admin.statusOnline"),
        style: statusStyles.online,
      };
    }
    if (apiStatus === "checking") {
      return {
        label: t("admin.statusChecking"),
        style: statusStyles.checking,
      };
    }
    return {
      label: t("admin.statusUnavailable"),
      style: statusStyles.offline,
    };
  }, [apiStatus, t, statusStyles]);

  const dbBadge = useMemo(() => {
    if (dbStatus === "online") {
      return {
        label: t("admin.statusActive"),
        style: statusStyles.online,
      };
    }
    if (dbStatus === "checking") {
      return {
        label: t("admin.statusChecking"),
        style: statusStyles.checking,
      };
    }
    return {
      label: t("admin.statusUnavailable"),
      style: statusStyles.offline,
    };
  }, [dbStatus, t, statusStyles]);

  const rebuildStatusBadge = useMemo(() => {
    if (!rebuildJob) {
      return {
        label: t("admin.systemRebuildIdle"),
        style: statusStyles.offline,
      };
    }
    if (rebuildJob.status === "RUNNING") {
      return {
        label: t("admin.systemRebuildRunning"),
        style: statusStyles.checking,
      };
    }
    if (rebuildJob.status === "COMPLETED") {
      return {
        label: t("admin.systemRebuildCompleted"),
        style: statusStyles.online,
      };
    }
    return {
      label: t("admin.systemRebuildFailed"),
      style: statusStyles.offline,
    };
  }, [rebuildJob, t, statusStyles]);

  const rebuildProgress = useMemo(() => {
    if (!rebuildJob || rebuildJob.totalSteps <= 0) {
      return 0;
    }
    const raw = (rebuildJob.completedSteps / rebuildJob.totalSteps) * 100;
    return Math.max(0, Math.min(100, Math.round(raw)));
  }, [rebuildJob]);

  const searchIndexStatusBadge = useMemo(() => {
    if (!searchIndexStatus) {
      return {
        label: t("admin.searchIndexRebuildIdle"),
        style: statusStyles.offline,
      };
    }
    if (searchIndexStatus.rebuildRunning) {
      return {
        label: t("admin.searchIndexRebuildRunning"),
        style: statusStyles.checking,
      };
    }
    if (searchIndexStatus.lastError) {
      return {
        label: t("admin.searchIndexRebuildFailed"),
        style: statusStyles.offline,
      };
    }
    if (searchIndexStatus.lastRebuildFinishedAt) {
      return {
        label: t("admin.searchIndexRebuildCompleted"),
        style: statusStyles.online,
      };
    }
    return {
      label: t("admin.searchIndexRebuildIdle"),
      style: statusStyles.offline,
    };
  }, [searchIndexStatus, t, statusStyles]);

  const searchIndexReadAliasDocs = useMemo(() => {
    const raw =
      searchIndexStatus?.openSearchHealth &&
      typeof searchIndexStatus.openSearchHealth === "object"
        ? (searchIndexStatus.openSearchHealth as Record<string, unknown>)[
            "read_alias_docs"
          ]
        : null;
    if (typeof raw === "number") {
      return raw;
    }
    if (typeof raw === "string") {
      const parsed = Number(raw);
      return Number.isFinite(parsed) ? parsed : 0;
    }
    return 0;
  }, [searchIndexStatus]);

  const instanceMigrationJob = instanceMigrationStatus?.job ?? null;

  const instanceMigrationStatusBadge = useMemo(() => {
    if (!instanceMigrationJob) {
      return {
        label: t("admin.instanceMigrationIdle"),
        style: statusStyles.offline,
      };
    }
    if (instanceMigrationJob.status === "QUEUED") {
      return {
        label: t("admin.instanceMigrationQueued"),
        style: statusStyles.checking,
      };
    }
    if (instanceMigrationJob.status === "RUNNING") {
      return {
        label: t("admin.instanceMigrationRunning"),
        style: statusStyles.checking,
      };
    }
    if (instanceMigrationJob.status === "PAUSED") {
      return {
        label: t("admin.instanceMigrationPaused"),
        style: statusStyles.offline,
      };
    }
    if (instanceMigrationJob.status === "COMPLETED") {
      return {
        label: t("admin.instanceMigrationCompleted"),
        style: statusStyles.online,
      };
    }
    return {
      label: t("admin.instanceMigrationFailed"),
      style: statusStyles.offline,
    };
  }, [instanceMigrationJob, statusStyles, t]);

  const instanceMigrationProgress = useMemo(() => {
    if (!instanceMigrationJob || instanceMigrationJob.totalItems <= 0) {
      return 0;
    }
    const raw =
      (instanceMigrationJob.processedItems / instanceMigrationJob.totalItems) *
      100;
    return Math.max(0, Math.min(100, Math.round(raw)));
  }, [instanceMigrationJob]);

  const instanceMigrationStorage = instanceMigrationStatus?.storage ?? null;
  const storageWarning =
    !!instanceMigrationStorage &&
    (!instanceMigrationStorage.available ||
      instanceMigrationStorage.risk === "WARNING" ||
      instanceMigrationStorage.risk === "CRITICAL" ||
      instanceMigrationStorage.risk === "UNKNOWN");

  const instanceMigrationStorageBadge = useMemo(() => {
    if (!instanceMigrationStorage) {
      return {
        label: t("admin.instanceMigrationStorageUnknown"),
        style: statusStyles.offline,
      };
    }
    if (instanceMigrationStorage.risk === "OK") {
      return {
        label: t("admin.instanceMigrationStorageOk"),
        style: statusStyles.online,
      };
    }
    if (instanceMigrationStorage.risk === "WARNING") {
      return {
        label: t("admin.instanceMigrationStorageWarning"),
        style: statusStyles.checking,
      };
    }
    if (instanceMigrationStorage.risk === "CRITICAL") {
      return {
        label: t("admin.instanceMigrationStorageCritical"),
        style: statusStyles.offline,
      };
    }
    return {
      label: t("admin.instanceMigrationStorageUnknown"),
      style: statusStyles.offline,
    };
  }, [instanceMigrationStorage, statusStyles, t]);

  const formatDuration = (seconds: number | null | undefined) => {
    if (seconds == null) return "-";
    const total = Math.max(0, Math.floor(seconds));
    const hours = Math.floor(total / 3600);
    const minutes = Math.floor((total % 3600) / 60);
    const secs = total % 60;
    if (hours > 0) {
      return `${hours}h ${minutes}m ${secs}s`;
    }
    if (minutes > 0) {
      return `${minutes}m ${secs}s`;
    }
    return `${secs}s`;
  };

  const formatBytes = (bytes: number | null | undefined) => {
    if (bytes == null || !Number.isFinite(bytes)) return "-";
    const units = ["B", "KiB", "MiB", "GiB", "TiB"];
    let value = Math.max(0, bytes);
    let unitIndex = 0;
    while (value >= 1024 && unitIndex < units.length - 1) {
      value /= 1024;
      unitIndex += 1;
    }
    const maximumFractionDigits = unitIndex === 0 ? 0 : value >= 10 ? 1 : 2;
    return `${value.toLocaleString(language === "de" ? "de-DE" : "en-US", {
      maximumFractionDigits,
    })} ${units[unitIndex]}`;
  };

  const formatNumber = (value: number | null | undefined) =>
    value == null || !Number.isFinite(value)
      ? "-"
      : value.toLocaleString(language === "de" ? "de-DE" : "en-US");

  const formatDatasetLabel = (dataset: string) =>
    dataset
      .split("_")
      .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
      .join(" ");

  return (
    <div className="grid gap-6">
      <Card className="stat-card">
        <CardHeader>
          <CardTitle
            className="card-title-text flex items-center gap-2"
            style={primaryTextStyle}
          >
            <AlertTriangle className="w-5 h-5" style={iconStyle} />
            {t("admin.errorPagesTests")}
          </CardTitle>
          <CardDescription
            className="card-description-text"
            style={secondaryTextStyle}
          >
            {t("admin.errorPagesFullDescription")}
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {errorTests.map((test) => (
              <Button
                key={test.page}
                variant="outline"
                onClick={() => navigate(test.page)}
                className="justify-start"
              >
                {test.label}
              </Button>
            ))}
          </div>
        </CardContent>
      </Card>

      <Card className="stat-card">
        <CardHeader>
          <CardTitle
            className="card-title-text flex items-center gap-2"
            style={primaryTextStyle}
          >
            <RefreshCw className="w-5 h-5" style={iconStyle} />
            {t("admin.instanceMigrationTitle")}
          </CardTitle>
          <CardDescription
            className="card-description-text"
            style={secondaryTextStyle}
          >
            {t("admin.instanceMigrationDescription")}
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {!instanceMigrationStatus?.featureEnabled ? (
            <div className="text-sm" style={secondaryTextStyle}>
              {t("admin.instanceMigrationDisabled")}
            </div>
          ) : (
            <>
              <div
                className="flex justify-between items-center p-3 border rounded-lg"
                style={borderStyle}
              >
                <span style={primaryTextStyle}>
                  {t("admin.instanceMigrationStatus")}
                </span>
                <Badge
                  className="border"
                  style={instanceMigrationStatusBadge.style}
                >
                  {instanceMigrationStatusBadge.label}
                </Badge>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <div className="space-y-1">
                  <Label htmlFor="instance-migration-source-url">
                    {t("admin.instanceMigrationSourceUrl")}
                  </Label>
                  <Input
                    id="instance-migration-source-url"
                    value={instanceMigrationSourceBaseUrl}
                    onChange={(e) => setInstanceMigrationSourceBaseUrl(e.target.value)}
                    placeholder="http://132.231.12.58:3000"
                  />
                </div>
                <div className="space-y-1">
                  <Label htmlFor="instance-migration-shared-token">
                    {t("admin.instanceMigrationSharedToken")}
                  </Label>
                  <Input
                    id="instance-migration-shared-token"
                    value={instanceMigrationSharedToken}
                    onChange={(e) => setInstanceMigrationSharedToken(e.target.value)}
                    placeholder={t("admin.instanceMigrationOptionalOverride")}
                  />
                </div>
                <div className="space-y-1">
                  <Label htmlFor="instance-migration-source-admin-email">
                    {t("admin.instanceMigrationSourceAdminEmail")}
                  </Label>
                  <Input
                    id="instance-migration-source-admin-email"
                    type="email"
                    value={instanceMigrationSourceAdminEmail}
                    onChange={(e) => setInstanceMigrationSourceAdminEmail(e.target.value)}
                    placeholder="webkurator@fim.uni-passau.de"
                  />
                </div>
                <div className="space-y-1">
                  <Label htmlFor="instance-migration-source-admin-password">
                    {t("admin.instanceMigrationSourceAdminPassword")}
                  </Label>
                  <Input
                    id="instance-migration-source-admin-password"
                    type="password"
                    value={instanceMigrationSourceAdminPassword}
                    onChange={(e) => setInstanceMigrationSourceAdminPassword(e.target.value)}
                    placeholder={t("admin.instanceMigrationOptionalOverride")}
                  />
                </div>
                <div className="space-y-1">
                  <Label htmlFor="instance-migration-page-size">
                    {t("admin.instanceMigrationPageSize")}
                  </Label>
                  <Input
                    id="instance-migration-page-size"
                    type="number"
                    min={1}
                    step={100}
                    value={instanceMigrationPageSize}
                    onChange={(e) => setInstanceMigrationPageSize(e.target.value)}
                    placeholder="1000"
                  />
                </div>
                <div className="space-y-1">
                  <Label htmlFor="instance-migration-timeout-ms">
                    {t("admin.instanceMigrationRequestTimeoutMs")}
                  </Label>
                  <Input
                    id="instance-migration-timeout-ms"
                    type="number"
                    min={500}
                    step={500}
                    value={instanceMigrationRequestTimeoutMs}
                    onChange={(e) => setInstanceMigrationRequestTimeoutMs(e.target.value)}
                    placeholder="3600000"
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <div className="p-3 border rounded-lg" style={borderStyle}>
                  <div className="text-xs" style={secondaryTextStyle}>
                    {t("admin.instanceMigrationSourceUrl")}
                  </div>
                  <div className="text-sm font-medium break-all" style={primaryTextStyle}>
                    {instanceMigrationStatus?.sourceBaseUrl || "-"}
                  </div>
                </div>
                <div className="p-3 border rounded-lg" style={borderStyle}>
                  <div className="text-xs" style={secondaryTextStyle}>
                    {t("admin.instanceMigrationPageSize")}
                  </div>
                  <div className="text-sm font-medium" style={primaryTextStyle}>
                    {instanceMigrationStatus?.pageSize ?? "-"}
                  </div>
                </div>
              </div>

              <div className="space-y-2">
                <Progress value={instanceMigrationProgress} />
                <div className="flex justify-between text-xs" style={secondaryTextStyle}>
                  <span>
                    {instanceMigrationJob
                      ? `${instanceMigrationJob.processedItems}/${instanceMigrationJob.totalItems}`
                      : "0/0"}
                  </span>
                  <span>{instanceMigrationProgress}%</span>
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <div className="p-3 border rounded-lg" style={borderStyle}>
                  <div className="text-xs" style={secondaryTextStyle}>
                    {t("admin.instanceMigrationCurrentPhase")}
                  </div>
                  <div className="text-sm font-medium" style={primaryTextStyle}>
                    {instanceMigrationJob?.currentPhase ||
                      t("admin.instanceMigrationNoRunYet")}
                  </div>
                </div>
                <div className="p-3 border rounded-lg" style={borderStyle}>
                  <div className="text-xs" style={secondaryTextStyle}>
                    {t("admin.instanceMigrationCurrentDataset")}
                  </div>
                  <div className="text-sm font-medium" style={primaryTextStyle}>
                    {instanceMigrationJob?.currentDataset
                      ? formatDatasetLabel(instanceMigrationJob.currentDataset)
                      : "-"}
                  </div>
                </div>
                <div className="p-3 border rounded-lg" style={borderStyle}>
                  <div className="text-xs" style={secondaryTextStyle}>
                    {t("admin.instanceMigrationElapsed")}
                  </div>
                  <div className="text-sm font-medium" style={primaryTextStyle}>
                    {formatDuration(instanceMigrationJob?.elapsedSeconds)}
                  </div>
                </div>
                <div className="p-3 border rounded-lg" style={borderStyle}>
                  <div className="text-xs" style={secondaryTextStyle}>
                    {t("admin.instanceMigrationEta")}
                  </div>
                  <div className="text-sm font-medium" style={primaryTextStyle}>
                    {formatDuration(instanceMigrationJob?.etaSeconds)}
                  </div>
                </div>
              </div>

              <div className="text-sm" style={secondaryTextStyle}>
                {instanceMigrationJob?.message ||
                  t("admin.instanceMigrationNoRunYet")}
              </div>
              <div className="text-xs" style={secondaryTextStyle}>
                {t("admin.instanceMigrationOverrideHint")}
              </div>
              <div className="text-sm" style={secondaryTextStyle}>
                {t("admin.instanceMigrationLastCheckpoint")}:{" "}
                {instanceMigrationJob?.lastCheckpointAt
                  ? new Date(instanceMigrationJob.lastCheckpointAt).toLocaleString(
                      language === "de" ? "de-DE" : "en-US"
                    )
                  : "-"}
              </div>
              {instanceMigrationJob?.errorMessage ? (
                <div className="text-sm text-red-600">
                  {instanceMigrationJob.errorMessage}
                </div>
              ) : null}

              {instanceMigrationStorage ? (
                <div className="space-y-3">
                  <div className="flex items-center justify-between gap-2">
                    <div
                      className="text-sm font-medium flex items-center gap-2"
                      style={primaryTextStyle}
                    >
                      <HardDrive className="w-4 h-4" style={iconStyle} />
                      {t("admin.instanceMigrationStorageTitle")}
                    </div>
                    <Badge
                      className="border"
                      style={instanceMigrationStorageBadge.style}
                    >
                      {instanceMigrationStorageBadge.label}
                    </Badge>
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                    <div className="p-3 border rounded-lg" style={borderStyle}>
                      <div className="text-xs" style={secondaryTextStyle}>
                        {t("admin.instanceMigrationStorageDatabase")}
                      </div>
                      <div className="text-sm font-medium" style={primaryTextStyle}>
                        {formatBytes(instanceMigrationStorage.databaseBytes)}
                      </div>
                    </div>
                    <div className="p-3 border rounded-lg" style={borderStyle}>
                      <div className="text-xs" style={secondaryTextStyle}>
                        {t("admin.instanceMigrationStorageMeasured")}
                      </div>
                      <div className="text-sm font-medium" style={primaryTextStyle}>
                        {formatBytes(instanceMigrationStorage.measuredMigrationBytes)}
                      </div>
                    </div>
                    <div className="p-3 border rounded-lg" style={borderStyle}>
                      <div className="text-xs" style={secondaryTextStyle}>
                        {t("admin.instanceMigrationStorageProjected")}
                      </div>
                      <div className="text-sm font-medium" style={primaryTextStyle}>
                        {formatBytes(instanceMigrationStorage.projectedMigrationBytes)}
                      </div>
                    </div>
                    <div className="p-3 border rounded-lg" style={borderStyle}>
                      <div className="text-xs" style={secondaryTextStyle}>
                        {t("admin.instanceMigrationStorageQuota")}
                      </div>
                      <div className="text-sm font-medium" style={primaryTextStyle}>
                        {formatBytes(instanceMigrationStorage.approvedQuotaBytes)}
                      </div>
                    </div>
                    <div className="p-3 border rounded-lg" style={borderStyle}>
                      <div className="text-xs" style={secondaryTextStyle}>
                        {t("admin.instanceMigrationStorageAdditional")}
                      </div>
                      <div className="text-sm font-medium" style={primaryTextStyle}>
                        {formatBytes(instanceMigrationStorage.projectedAdditionalBytes)}
                      </div>
                    </div>
                    <div className="p-3 border rounded-lg" style={borderStyle}>
                      <div className="text-xs" style={secondaryTextStyle}>
                        {t("admin.instanceMigrationStorageHeadroom")}
                      </div>
                      <div className="text-sm font-medium" style={primaryTextStyle}>
                        {formatBytes(instanceMigrationStorage.requiredHeadroomBytes)}
                      </div>
                    </div>
                  </div>
                  <div
                    className={
                      instanceMigrationStorage.risk === "CRITICAL"
                        ? "text-sm text-red-600"
                        : "text-sm"
                    }
                    style={
                      instanceMigrationStorage.risk === "CRITICAL"
                        ? undefined
                        : secondaryTextStyle
                    }
                  >
                    {instanceMigrationStorage.message}
                  </div>
                  <div className="text-xs" style={secondaryTextStyle}>
                    {t("admin.instanceMigrationStorageQuotaHint")}
                  </div>
                  {instanceMigrationStorage.parts?.length ? (
                    <div className="space-y-2 max-h-[20rem] overflow-y-auto pr-1">
                      {instanceMigrationStorage.parts.map((part) => (
                        <div
                          key={part.key}
                          className="p-3 border rounded-lg space-y-2"
                          style={borderStyle}
                        >
                          <div className="flex items-center justify-between gap-2">
                            <div
                              className="text-sm font-medium"
                              style={primaryTextStyle}
                            >
                              {formatDatasetLabel(part.key)}
                            </div>
                            <Badge
                              className="border"
                              style={
                                part.risk === "OK"
                                  ? statusStyles.online
                                  : part.risk === "WARNING"
                                    ? statusStyles.checking
                                    : statusStyles.offline
                              }
                            >
                              {part.risk}
                            </Badge>
                          </div>
                          <div
                            className="grid grid-cols-1 md:grid-cols-4 gap-2 text-xs"
                            style={secondaryTextStyle}
                          >
                            <div>
                              {t("admin.instanceMigrationStorageRows")}:{" "}
                              {formatNumber(part.processed)}/
                              {part.sourceTotal > 0
                                ? formatNumber(part.sourceTotal)
                                : formatNumber(part.currentRows)}
                            </div>
                            <div>
                              {t("admin.instanceMigrationStorageCurrent")}:{" "}
                              {formatBytes(part.totalBytes)}
                            </div>
                            <div>
                              {t("admin.instanceMigrationStorageProjectedShort")}:{" "}
                              {formatBytes(part.projectedTotalBytes)}
                            </div>
                            <div>
                              {t("admin.instanceMigrationStorageAdditionalShort")}:{" "}
                              {formatBytes(part.projectedAdditionalBytes)}
                            </div>
                          </div>
                          <div className="text-xs" style={secondaryTextStyle}>
                            {t("admin.instanceMigrationStorageTableIndexes")}:{" "}
                            {formatBytes(part.tableBytes)} /{" "}
                            {formatBytes(part.indexBytes)}
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : null}
                </div>
              ) : null}

              <div className="flex flex-wrap gap-2">
                <AlertDialog
                  open={instanceMigrationConfirmOpen}
                  onOpenChange={setInstanceMigrationConfirmOpen}
                >
                  <AlertDialogTrigger asChild>
                    <Button
                      className="gap-2"
                      disabled={
                        startingInstanceMigration ||
                        instanceMigrationJob?.status === "QUEUED" ||
                        instanceMigrationJob?.status === "RUNNING"
                      }
                    >
                      {startingInstanceMigration ||
                      instanceMigrationJob?.status === "QUEUED" ||
                      instanceMigrationJob?.status === "RUNNING" ? (
                        <Loader2 className="w-4 h-4 animate-spin" />
                      ) : (
                        <Play className="w-4 h-4" />
                      )}
                      {instanceMigrationJob?.status === "PAUSED" ||
                      instanceMigrationJob?.status === "FAILED"
                        ? t("admin.instanceMigrationResume")
                        : t("admin.instanceMigrationStart")}
                    </Button>
                  </AlertDialogTrigger>
                  <AlertDialogContent>
                    <AlertDialogHeader>
                      <AlertDialogTitle>
                        {t("admin.instanceMigrationConfirmTitle")}
                      </AlertDialogTitle>
                      <AlertDialogDescription className="space-y-2">
                        <span className="block">
                          {t("admin.instanceMigrationConfirmDescription")}
                        </span>
                        {storageWarning && instanceMigrationStorage ? (
                          <span className="block text-red-600">
                            {t("admin.instanceMigrationStorageResumeWarning")}{" "}
                            {instanceMigrationStorage.message}
                          </span>
                        ) : null}
                      </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                      <AlertDialogCancel>{t("common.cancel")}</AlertDialogCancel>
                      <AlertDialogAction
                        onClick={handleStartInstanceMigration}
                        disabled={
                          startingInstanceMigration ||
                          instanceMigrationJob?.status === "QUEUED" ||
                          instanceMigrationJob?.status === "RUNNING"
                        }
                      >
                        {t("admin.instanceMigrationConfirmAction")}
                      </AlertDialogAction>
                    </AlertDialogFooter>
                  </AlertDialogContent>
                </AlertDialog>
                <Button
                  variant="outline"
                  className="gap-2"
                  onClick={() => loadInstanceMigrationStatus()}
                >
                  <RefreshCw className="w-4 h-4" />
                  {t("admin.instanceMigrationRefresh")}
                </Button>
              </div>

              <div className="space-y-2">
                <div className="text-sm font-medium" style={primaryTextStyle}>
                  {t("admin.instanceMigrationDatasetProgress")}
                </div>
                {instanceMigrationJob?.datasets?.length ? (
                  <div className="space-y-2 max-h-[22rem] overflow-y-auto pr-1">
                    {instanceMigrationJob.datasets.map((dataset) => (
                      <div
                        key={dataset.dataset}
                        className="p-3 border rounded-lg space-y-1"
                        style={borderStyle}
                      >
                        <div className="flex items-center justify-between gap-2">
                          <div className="text-sm font-medium" style={primaryTextStyle}>
                            {formatDatasetLabel(dataset.dataset)}
                          </div>
                          <Badge
                            className="border"
                            style={dataset.completed ? statusStyles.online : statusStyles.checking}
                          >
                            {dataset.completed
                              ? t("admin.instanceMigrationCompleted")
                              : t("admin.instanceMigrationProcessed")}
                          </Badge>
                        </div>
                        <div className="text-xs" style={secondaryTextStyle}>
                          {t("admin.instanceMigrationProcessed")}: {dataset.processed}/
                          {dataset.sourceTotal}
                        </div>
                        <div className="text-xs" style={secondaryTextStyle}>
                          {t("admin.instanceMigrationCounts")}: {dataset.inserted} /{" "}
                          {dataset.updated} / {dataset.skipped} / {dataset.failed}
                        </div>
                        <div className="text-xs" style={secondaryTextStyle}>
                          {t("admin.instanceMigrationLastCheckpoint")}:{" "}
                          {dataset.lastCheckpointAt
                            ? new Date(dataset.lastCheckpointAt).toLocaleString(
                                language === "de" ? "de-DE" : "en-US"
                              )
                            : "-"}
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-sm" style={secondaryTextStyle}>
                    {t("admin.instanceMigrationEmptyDatasets")}
                  </div>
                )}
              </div>
            </>
          )}
        </CardContent>
      </Card>

      <Card className="stat-card">
        <CardHeader>
          <CardTitle
            className="card-title-text flex items-center gap-2"
            style={primaryTextStyle}
          >
            <RefreshCw className="w-5 h-5" style={iconStyle} />
            {t("admin.searchIndexRebuildTitle")}
          </CardTitle>
          <CardDescription
            className="card-description-text"
            style={secondaryTextStyle}
          >
            {t("admin.searchIndexRebuildDescription")}
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div
            className="flex justify-between items-center p-3 border rounded-lg"
            style={borderStyle}
          >
            <span style={primaryTextStyle}>
              {t("admin.searchIndexRebuildStatus")}
            </span>
            <Badge className="border" style={searchIndexStatusBadge.style}>
              {searchIndexStatusBadge.label}
            </Badge>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div className="p-3 border rounded-lg" style={borderStyle}>
              <div className="text-xs" style={secondaryTextStyle}>
                {t("admin.searchIndexEngineMode")}
              </div>
              <div className="text-sm font-medium" style={primaryTextStyle}>
                {searchIndexStatus?.engineMode ?? "-"}
              </div>
            </div>
            <div className="p-3 border rounded-lg" style={borderStyle}>
              <div className="text-xs" style={secondaryTextStyle}>
                {t("admin.searchIndexDocsOnReadAlias")}
              </div>
              <div className="text-sm font-medium" style={primaryTextStyle}>
                {searchIndexReadAliasDocs.toLocaleString(language === "de" ? "de-DE" : "en-US")}
              </div>
            </div>
            <div className="p-3 border rounded-lg" style={borderStyle}>
              <div className="text-xs" style={secondaryTextStyle}>
                {t("admin.searchIndexOutboxPending")}
              </div>
              <div className="text-sm font-medium" style={primaryTextStyle}>
                {(searchIndexStatus?.outboxPending ?? 0).toLocaleString(
                  language === "de" ? "de-DE" : "en-US"
                )}
              </div>
            </div>
            <div className="p-3 border rounded-lg" style={borderStyle}>
              <div className="text-xs" style={secondaryTextStyle}>
                {t("admin.searchIndexOutboxFailed")}
              </div>
              <div className="text-sm font-medium" style={primaryTextStyle}>
                {(searchIndexStatus?.outboxFailed ?? 0).toLocaleString(
                  language === "de" ? "de-DE" : "en-US"
                )}
              </div>
            </div>
          </div>

          <div className="text-sm" style={secondaryTextStyle}>
            {searchIndexStatus?.rebuildRunning
              ? t("admin.searchIndexRebuildRunningHint")
              : searchIndexStatus?.lastRebuildFinishedAt
                ? `${t("admin.searchIndexRebuildLastFinishedAt")} ${new Date(
                    searchIndexStatus.lastRebuildFinishedAt
                  ).toLocaleString(language === "de" ? "de-DE" : "en-US")}`
                : t("admin.searchIndexRebuildNoRunYet")}
          </div>

          {searchIndexStatus?.lastError ? (
            <div className="text-sm text-red-600">{searchIndexStatus.lastError}</div>
          ) : null}

          <div className="flex flex-wrap gap-2">
            <AlertDialog
              open={searchIndexRebuildConfirmOpen}
              onOpenChange={setSearchIndexRebuildConfirmOpen}
            >
              <AlertDialogTrigger asChild>
                <Button
                  className="gap-2"
                  disabled={
                    startingSearchIndexRebuild ||
                    searchIndexStatus?.rebuildRunning
                  }
                >
                  {startingSearchIndexRebuild ||
                  searchIndexStatus?.rebuildRunning ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  ) : (
                    <Play className="w-4 h-4" />
                  )}
                  {t("admin.searchIndexRebuildStart")}
                </Button>
              </AlertDialogTrigger>
              <AlertDialogContent>
                <AlertDialogHeader>
                  <AlertDialogTitle>
                    {t("admin.searchIndexRebuildConfirmTitle")}
                  </AlertDialogTitle>
                  <AlertDialogDescription>
                    {t("admin.searchIndexRebuildConfirmDescription")}
                  </AlertDialogDescription>
                </AlertDialogHeader>
                <AlertDialogFooter>
                  <AlertDialogCancel>{t("common.cancel")}</AlertDialogCancel>
                  <AlertDialogAction
                    onClick={handleStartSearchIndexRebuild}
                    disabled={
                      startingSearchIndexRebuild ||
                      searchIndexStatus?.rebuildRunning
                    }
                  >
                    {t("admin.searchIndexRebuildConfirmAction")}
                  </AlertDialogAction>
                </AlertDialogFooter>
              </AlertDialogContent>
            </AlertDialog>
            <Button
              variant="outline"
              className="gap-2"
              onClick={() => loadSearchIndexStatus()}
            >
              <RefreshCw className="w-4 h-4" />
              {t("admin.searchIndexRebuildRefresh")}
            </Button>
          </div>
        </CardContent>
      </Card>

      <Card className="stat-card">
        <CardHeader>
          <CardTitle
            className="card-title-text flex items-center gap-2"
            style={primaryTextStyle}
          >
            <Play className="w-5 h-5" style={iconStyle} />
            {t("admin.systemRebuildTitle")}
          </CardTitle>
          <CardDescription
            className="card-description-text"
            style={secondaryTextStyle}
          >
            {t("admin.systemRebuildDescription")}
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div
            className="flex justify-between items-center p-3 border rounded-lg"
            style={borderStyle}
          >
            <span style={primaryTextStyle}>{t("admin.systemRebuildStatus")}</span>
            <Badge className="border" style={rebuildStatusBadge.style}>
              {rebuildStatusBadge.label}
            </Badge>
          </div>
          <div className="space-y-2">
            <Progress value={rebuildProgress} />
            <div className="flex justify-between text-xs" style={secondaryTextStyle}>
              <span>
                {rebuildJob
                  ? `${rebuildJob.completedSteps}/${rebuildJob.totalSteps}`
                  : "0/0"}
              </span>
              <span>{rebuildProgress}%</span>
            </div>
          </div>
          <div className="text-sm" style={secondaryTextStyle}>
            {rebuildJob?.message || t("admin.systemRebuildNoRunYet")}
          </div>
          {rebuildJob?.errorMessage ? (
            <div className="text-sm text-red-600">{rebuildJob.errorMessage}</div>
          ) : null}
          <div className="flex flex-wrap gap-2">
            <AlertDialog
              open={rebuildConfirmOpen}
              onOpenChange={setRebuildConfirmOpen}
            >
              <AlertDialogTrigger asChild>
                <Button
                  className="gap-2"
                  disabled={startingRebuild || rebuildJob?.status === "RUNNING"}
                >
                  {startingRebuild || rebuildJob?.status === "RUNNING" ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  ) : (
                    <Play className="w-4 h-4" />
                  )}
                  {t("admin.systemRebuildStart")}
                </Button>
              </AlertDialogTrigger>
              <AlertDialogContent>
                <AlertDialogHeader>
                  <AlertDialogTitle>{t("admin.systemRebuildConfirmTitle")}</AlertDialogTitle>
                  <AlertDialogDescription>
                    {t("admin.systemRebuildConfirmDescription")}
                  </AlertDialogDescription>
                </AlertDialogHeader>
                <AlertDialogFooter>
                  <AlertDialogCancel>{t("common.cancel")}</AlertDialogCancel>
                  <AlertDialogAction
                    onClick={handleStartRebuild}
                    disabled={startingRebuild || rebuildJob?.status === "RUNNING"}
                  >
                    {t("admin.systemRebuildConfirmAction")}
                  </AlertDialogAction>
                </AlertDialogFooter>
              </AlertDialogContent>
            </AlertDialog>
            <Button
              variant="outline"
              className="gap-2"
              onClick={() => loadLatestRebuildJob()}
            >
              <RefreshCw className="w-4 h-4" />
              {t("admin.systemRebuildRefresh")}
            </Button>
          </div>
        </CardContent>
      </Card>

      <Card className="stat-card">
        <CardHeader>
          <CardTitle
            className="card-title-text flex items-center gap-2"
            style={primaryTextStyle}
          >
            <Undo2 className="w-5 h-5" style={iconStyle} />
            {t("admin.actionLogTitle")}
          </CardTitle>
          <CardDescription
            className="card-description-text"
            style={secondaryTextStyle}
          >
            {t("admin.actionLogFullDescription")}
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="space-y-3">
            {loadingActivities ? (
              <div className="flex justify-center p-4">
                <Loader2 className="h-6 w-6 animate-spin" style={iconStyle} />
              </div>
            ) : activities.length === 0 ? (
              <div className="text-center p-4 text-sm" style={secondaryTextStyle}>
                {t("admin.systemActivityEmpty")}
              </div>
            ) : (
              <>
                {paginatedActivities.map((activity) => (
                  <div
                    key={activity.id}
                    className="flex justify-between items-center p-3 border rounded-lg animate-in fade-in slide-in-from-bottom-2 duration-300"
                    style={borderStyle}
                  >
                    <div>
                      <p style={primaryTextStyle}>{getActionDescription(activity)}</p>
                      <p className="text-xs" style={secondaryTextStyle}>
                        {formatDistanceToNow(new Date(activity.occurredAt), {
                          addSuffix: true,
                          locale: language === "de" ? de : enUS,
                        })}{" "}
                        •{" "}
                        <span
                          className={activity.userName ? "font-medium" : ""}
                          style={activity.userName ? primaryTextStyle : undefined}
                        >
                          {activity.userName || "System"}
                        </span>
                      </p>
                    </div>

                    <Button
                      variant="outline"
                      size="sm"
                      className="gap-2"
                      onClick={() => handleUndo(activity.id)}
                    >
                      <Undo2 className="w-4 h-4" />
                      {t("common.undo")}
                    </Button>
                  </div>
                ))}

                <div className="flex items-center justify-between pt-4 border-t mt-4">
                  <div className="text-xs" style={secondaryTextStyle}>
                    {t("common.page")} {currentPage} / {totalPages}
                  </div>
                  <div className="flex items-center gap-2">
                    <Button
                      variant="outline"
                      size="icon"
                      className="h-8 w-8"
                      onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
                      disabled={currentPage === 1}
                    >
                      <ChevronLeft className="h-4 w-4" style={iconStyle} />
                    </Button>
                    <Button
                      variant="outline"
                      size="icon"
                      className="h-8 w-8"
                      onClick={() =>
                        setCurrentPage((p) => Math.min(totalPages, p + 1))
                      }
                      disabled={currentPage === totalPages}
                    >
                      <ChevronRight className="h-4 w-4" style={iconStyle} />
                    </Button>
                  </div>
                </div>
              </>
            )}
          </div>
        </CardContent>
      </Card>

      <Card className="stat-card">
        <CardHeader>
          <CardTitle className="card-title-text" style={primaryTextStyle}>
            {t("admin.systemStatusTitle")}
          </CardTitle>
          <CardDescription
            className="card-description-text"
            style={secondaryTextStyle}
          >
            {t("admin.systemStatusFullDescription")}
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="space-y-3">
            <div
              className="flex justify-between items-center p-3 border rounded-lg"
              style={borderStyle}
            >
              <span style={primaryTextStyle}>{t("admin.databaseConnection")}</span>
              <Badge className="border" style={dbBadge.style}>
                {dbBadge.label}
              </Badge>
            </div>
            <div
              className="flex justify-between items-center p-3 border rounded-lg"
              style={borderStyle}
            >
              <span style={primaryTextStyle}>{t("admin.apiStatus")}</span>
              <Badge className="border" style={apiBadge.style}>
                {apiBadge.label}
              </Badge>
            </div>
          </div>
        </CardContent>
      </Card>

      <Card className="stat-card">
        <CardHeader>
          <CardTitle
            className="card-title-text flex items-center gap-2"
            style={primaryTextStyle}
          >
            <FileText className="w-5 h-5" style={iconStyle} />
            {t("admin.systemLogsTitle")}
          </CardTitle>
          <CardDescription
            className="card-description-text"
            style={secondaryTextStyle}
          >
            {t("admin.systemLogsDescription")}
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
            <div className="space-y-1">
              <Label>{t("admin.systemLogsSource")}</Label>
              <Select
                value={logSource || "__all__"}
                onValueChange={(value) =>
                  setLogSource(value === "__all__" ? "" : (value as any))
                }
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="__all__">{t("admin.systemLogsAll")}</SelectItem>
                  <SelectItem value="application">
                    {t("admin.systemLogsSourceApplication")}
                  </SelectItem>
                  <SelectItem value="parquet">
                    {t("admin.systemLogsSourceParquet")}
                  </SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1">
              <Label>{t("admin.systemLogsLevel")}</Label>
              <Select
                value={logLevel || "__all__"}
                onValueChange={(value) =>
                  setLogLevel(value === "__all__" ? "" : (value as any))
                }
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="__all__">{t("admin.systemLogsAll")}</SelectItem>
                  <SelectItem value="ERROR">ERROR</SelectItem>
                  <SelectItem value="WARN">WARN</SelectItem>
                  <SelectItem value="INFO">INFO</SelectItem>
                  <SelectItem value="DEBUG">DEBUG</SelectItem>
                  <SelectItem value="TRACE">TRACE</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1">
              <Label>{t("admin.systemLogsLogger")}</Label>
              <Input
                value={logLoggerContains}
                onChange={(e) => setLogLoggerContains(e.target.value)}
                placeholder={t("admin.systemLogsLoggerPlaceholder")}
              />
            </div>
            <div className="space-y-1">
              <Label>{t("admin.systemLogsQuery")}</Label>
              <Input
                value={logQuery}
                onChange={(e) => setLogQuery(e.target.value)}
                placeholder={t("admin.systemLogsQueryPlaceholder")}
              />
            </div>
          </div>

          <div className="flex flex-wrap gap-2">
            <Button className="gap-2" onClick={handleApplyLogFilters}>
              <Filter className="w-4 h-4" />
              {t("admin.systemLogsApply")}
            </Button>
            <Button
              variant="outline"
              className="gap-2"
              onClick={() => loadLogs(logsPage)}
            >
              <RefreshCw className="w-4 h-4" />
              {t("admin.systemLogsRefresh")}
            </Button>
          </div>

          <div className="text-xs" style={secondaryTextStyle}>
            {t("admin.systemLogsResults")}: {logsTotalElements}
          </div>

          <div className="space-y-2 max-h-[26rem] overflow-y-auto pr-1">
            {loadingLogs ? (
              <div className="flex justify-center p-4">
                <Loader2 className="h-6 w-6 animate-spin" style={iconStyle} />
              </div>
            ) : logEntries.length === 0 ? (
              <div className="text-center p-4 text-sm" style={secondaryTextStyle}>
                {t("admin.systemLogsEmpty")}
              </div>
            ) : (
              logEntries.map((entry, index) => (
                <div
                  key={`${entry.timestamp}-${index}`}
                  className="p-3 border rounded-lg space-y-1"
                  style={borderStyle}
                >
                  <div className="flex flex-wrap items-center gap-2 text-xs">
                    <Badge variant="outline">{entry.level}</Badge>
                    <Badge variant="outline">{entry.source}</Badge>
                    <span style={secondaryTextStyle}>{entry.timestamp}</span>
                    <span className="font-medium" style={primaryTextStyle}>
                      {entry.logger}
                    </span>
                  </div>
                  <p className="text-sm break-words" style={primaryTextStyle}>
                    {entry.message}
                  </p>
                </div>
              ))
            )}
          </div>

          <div className="flex items-center justify-between">
            <div className="text-xs" style={secondaryTextStyle}>
              {t("common.page")} {logsPage} / {logsTotalPages}
            </div>
            <div className="flex items-center gap-2">
              <Button
                variant="outline"
                size="icon"
                className="h-8 w-8"
                onClick={() => loadLogs(Math.max(1, logsPage - 1))}
                disabled={logsPage <= 1 || loadingLogs}
              >
                <ChevronLeft className="h-4 w-4" style={iconStyle} />
              </Button>
              <Button
                variant="outline"
                size="icon"
                className="h-8 w-8"
                onClick={() => loadLogs(Math.min(logsTotalPages, logsPage + 1))}
                disabled={logsPage >= logsTotalPages || loadingLogs}
              >
                <ChevronRight className="h-4 w-4" style={iconStyle} />
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
